import { z } from "zod";
import { topicNarrationProviderSchema } from "./topic-narration-provider";

export const captionHighlightModeSchema = z.enum([
	"word",
	"sentence",
	"line",
	"paragraph",
]);

export type CaptionHighlightMode = z.infer<typeof captionHighlightModeSchema>;

export const captionCueWordSchema = z.object({
	text: z.string(),
	startMs: z.number().nonnegative(),
	endMs: z.number().nonnegative(),
});

export const captionCueSchema = z.object({
	id: z.string(),
	startMs: z.number().nonnegative(),
	endMs: z.number().nonnegative(),
	text: z.string(),
	words: z.array(captionCueWordSchema).optional(),
});

export type CaptionCueWord = z.infer<typeof captionCueWordSchema>;
export type CaptionCue = z.infer<typeof captionCueSchema>;

export const topicNarrationCardStatusSchema = z.enum([
	"NONE",
	"QUEUED",
	"GENERATING",
	"READY",
	"PARTIAL",
	"STALE",
	"FAILED",
]);

export type TopicNarrationCardStatus = z.infer<
	typeof topicNarrationCardStatusSchema
>;

export const topicCardNarrationManifestEntrySchema = z.object({
	contentFingerprint: z.string(),
	audioAssetId: z.string(),
	vttAssetId: z.string(),
	durationMs: z.number().int().positive(),
	targetDurationMs: z.number().int().positive(),
	captionsSourceText: z.string().optional(),
	status: topicNarrationCardStatusSchema.optional(),
});

export type NarrationAudioSourceManifestEntry = z.infer<
	typeof topicCardNarrationManifestEntrySchema
>;

export const topicNarrationManifestV1Schema = z.object({
	version: z.literal(1),
	provider: topicNarrationProviderSchema,
	voiceId: z.string(),
	cards: z.record(z.string(), topicCardNarrationManifestEntrySchema),
});

export type TopicNarrationManifestV1 = z.infer<
	typeof topicNarrationManifestV1Schema
>;

export const topicCardNarrationManifestEntryV2Schema =
	topicCardNarrationManifestEntrySchema.extend({
		bodyDurationMs: z.number().int().positive(),
		summaryDurationMs: z.number().int().positive().optional(),
		summaryAudioAssetId: z.string().optional(),
		summaryVttAssetId: z.string().optional(),
		summarySectionStartMs: z.number().int().nonnegative().optional(),
	});

export type NarrationAudioSourceManifestEntryV2 = z.infer<
	typeof topicCardNarrationManifestEntryV2Schema
>;

export const topicNarrationManifestV2Schema = z.object({
	version: z.literal(2),
	provider: topicNarrationProviderSchema,
	voiceId: z.string(),
	cards: z.record(z.string(), topicCardNarrationManifestEntryV2Schema),
});

export type TopicNarrationManifestV2 = z.infer<
	typeof topicNarrationManifestV2Schema
>;

export const topicBlockNarrationManifestEntrySchema = z.object({
	contentFingerprint: z.string(),
	audioAssetId: z.string(),
	vttAssetId: z.string(),
	durationMs: z.number().int().positive(),
	targetDurationMs: z.number().int().positive(),
	offsetMs: z.number().int().nonnegative(),
	captionsSourceText: z.string().optional(),
});

export type TopicBlockNarrationManifestEntry = z.infer<
	typeof topicBlockNarrationManifestEntrySchema
>;

export const topicCardNarrationManifestEntryV3Schema =
	topicCardNarrationManifestEntryV2Schema.extend({
		blocks: z.record(z.string(), topicBlockNarrationManifestEntrySchema),
	});

export type NarrationAudioSourceManifestEntryV3 = z.infer<
	typeof topicCardNarrationManifestEntryV3Schema
>;

export type TopicNarrationManifestV3CardEntry =
	NarrationAudioSourceManifestEntryV3;

export const topicNarrationManifestV3Schema = z.object({
	version: z.literal(3),
	provider: topicNarrationProviderSchema,
	voiceId: z.string(),
	cards: z.record(z.string(), topicCardNarrationManifestEntryV3Schema),
});

export type TopicNarrationManifestV3 = z.infer<
	typeof topicNarrationManifestV3Schema
>;

/** Reads MediaNarratorAudioClip rows — never script-level synthesisScriptText. */
export const topicNarrationManifestV4Schema = z.object({
	version: z.literal(4),
	provider: topicNarrationProviderSchema,
	voiceId: z.string(),
	mediaRemixId: z.string(),
	narratorId: z.string(),
	cards: z.record(z.string(), topicCardNarrationManifestEntryV3Schema),
});

export type TopicNarrationManifestV4 = z.infer<
	typeof topicNarrationManifestV4Schema
>;

export const topicNarrationManifestSchema = z.discriminatedUnion("version", [
	topicNarrationManifestV1Schema,
	topicNarrationManifestV2Schema,
	topicNarrationManifestV3Schema,
	topicNarrationManifestV4Schema,
]);

export type TopicNarrationManifest = z.infer<
	typeof topicNarrationManifestSchema
>;

export function isBlockLevelTopicNarrationManifest(
	manifest: TopicNarrationManifest | null | undefined,
): manifest is TopicNarrationManifestV3 | TopicNarrationManifestV4 {
	return manifest?.version === 3 || manifest?.version === 4;
}
